// Customers search + list page.
//
// Behaviour:
//   - Search input is debounced (300ms) and stored in URL search params.
//   - Tier filter is a row of pills (All / 1 / 2 / 3) for quick toggling.
//   - Each result renders as a CustomerCard; clicking navigates to detail.
//   - Empty/loading/error states all rendered inline.
//   - W8-B: Active/Archived tab toggle, admin-gated "+ New customer" button
//     that opens an inline modal with the new-customer form (no route hop).

import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Loader2, AlertCircle, Plus, Search, X } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { CustomerCard } from "@/components/CustomerCard";
import { PermissionGate, ReadOnlyBanner } from "@/components/PermissionGate";
import { cn } from "@/lib/cn";
import { useMcp, mcp } from "@/lib/mcp";
import { CRM_CUSTOMERS_STATUS } from "@/lib/db-enums.generated";
import { CUSTOMER_TIERS } from "@/lib/static-enums";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useRealtimeRefresh } from "@/hooks/useRealtimeRefresh";
import { useFormDraft } from "@/hooks/useFormDraft";
import { useListStateRestore } from "@/hooks/useListStateRestore";

const DEBOUNCE_MS = 300;

const TIER_OPTIONS: Array<{ value: "" | "1" | "2" | "3"; label: string; help: string }> = [
  { value: "",  label: "All tiers",  help: "Customers across every tier" },
  { value: "1", label: "Tier 1",     help: "Strategic" },
  { value: "2", label: "Tier 2",     help: "Important" },
  { value: "3", label: "Tier 3",     help: "Minor" },
];

type ArchivedTab = "active" | "archived";

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function SkeletonCard() {
  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
        <div className="h-3 w-1/2 animate-pulse rounded bg-muted" />
        <div className="h-3 w-1/3 animate-pulse rounded bg-muted" />
      </CardContent>
    </Card>
  );
}

// ─── Inline new-customer modal ───────────────────────────────────────────
// Mirrors the standalone /forms/new-customer page but lives in a modal so
// admins can stay in context. Uses the same Zod schema + idempotency-key
// hash as the route page so back-pressure on the slug-uniqueness constraint
// behaves identically.

const newCustomerSchema = z.object({
  name: z.string().min(2, "Name is required (min 2 chars)"),
  slug: z.string().regex(
    /^[a-z0-9]+(-[a-z0-9]+)*$/,
    "Slug must be kebab-case (lowercase, digits, hyphens)",
  ),
  tier: z.enum(["1", "2", "3"]),
  status: z.enum(["active", "dormant", "archived"]),
  payment_terms: z.string().optional(),
  vat_number: z.string().optional(),
  notes: z.string().optional(),
});
type NewCustomerForm = z.infer<typeof newCustomerSchema>;

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}
async function sha256Hex(input: string): Promise<string> {
  const buf = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function NewCustomerModal({
  open, onClose, onCreated,
}: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const {
    register, handleSubmit, setValue, reset, watch, formState: { errors, dirtyFields },
  } = useForm<NewCustomerForm>({
    resolver: zodResolver(newCustomerSchema),
    defaultValues: { tier: "2", status: "active" },
  });
  const slugTouched = dirtyFields.slug;

  // Reset form whenever the modal opens fresh.
  useEffect(() => {
    if (open) {
      reset({ tier: "2", status: "active" });
      setSubmitError(null);
    }
  }, [open, reset]);

  // Draft persistence — keep typed entries alive across accidental refreshes
  // / modal closes. The hook only runs while the modal is open; the restore
  // happens after the open-reset above so a saved draft wins over the
  // empty defaults. Cleared after a successful submit.
  // eslint-disable-next-line react-hooks/incompatible-library -- react-hook-form's watch() re-renders on every field change via its own subscription outside compiler tracking; draftValues is intentionally re-read fresh each render for local draft persistence, never memoized.
  const draftValues = watch();
  const draft = useFormDraft<typeof draftValues>({
    formId: "customer:new",
    version: 1,
    values: draftValues,
    setValues: (v) => reset(v),
    enabled: open,
  });

  // Surface a quiet "Draft restored" toast the first time it actually
  // happens so the user knows the form was repopulated.
  useEffect(() => {
    if (draft.restored) {
      toast.message("Draft restored", {
        description: "Picked up where you left off — your saved fields and images are back.",
      });
    }
     
  }, [draft.restored]);

  if (!open) return null;

  const onSubmit = async (values: NewCustomerForm) => {
    setSubmitError(null);
    setSubmitting(true);
    try {
      const idempotency_key = await sha256Hex(`${values.slug}\n${values.name}`);
      // INTEGRATE: the orchestrator wires mcp.addCustomer to the deployed
      // edge function; calling .addCustomer here uses the existing TS client.
      const result = await mcp.addCustomer({
        slug: values.slug,
        name: values.name,
        tier: Number(values.tier) as 1 | 2 | 3,
        status: values.status,
        legal_entity: undefined,
        vat_number: values.vat_number || undefined,
        payment_terms: values.payment_terms || undefined,
        notes: values.notes || undefined,
        idempotency_key,
      });
      if (result.was_new) {
        toast.success(`Customer "${values.name}" created`);
      } else {
        toast.message(
          `Customer "${values.name}" already existed — opened existing record`,
        );
      }
      // Customer committed — drop the local draft so the next open of the
      // modal starts blank.
      await draft.clearDraft();
      onCreated();
      onClose();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setSubmitError(msg);
      toast.error("Could not create customer");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="new-customer-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <Card className="w-full max-w-2xl">
        <CardHeader className="flex-row items-start justify-between space-y-0">
          <CardTitle id="new-customer-modal-title">Add a customer</CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                {...register("name", {
                  onChange: (e) => {
                    if (!slugTouched) setValue("slug", slugify(e.target.value));
                  },
                })}
                placeholder="e.g. Truworths"
                autoFocus
              />
              {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="slug">Slug</Label>
              <Input id="slug" {...register("slug")} className="font-mono" placeholder="auto-generated from name" />
              <p className="text-xs text-muted-foreground">Lowercase, hyphens only.</p>
              {errors.slug && <p className="text-xs text-destructive">{errors.slug.message}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="tier">Tier</Label>
                <Select id="tier" {...register("tier")}>
                  {CUSTOMER_TIERS.map((t) => (
                    <option key={t.value} value={String(t.value)}>{t.label}</option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="status">Status</Label>
                <Select id="status" {...register("status")}>
                  {CRM_CUSTOMERS_STATUS.map((s) => (
                    <option key={s} value={s}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="payment_terms">Payment terms</Label>
                <Input id="payment_terms" {...register("payment_terms")} placeholder="e.g. 60 days" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="vat_number">VAT number</Label>
                <Input id="vat_number" {...register("vat_number")} className="font-mono" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="notes">Notes</Label>
              <Textarea id="notes" {...register("notes")} rows={3} />
            </div>

            {submitError && (
              <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <div>
                  <p className="font-medium">Submission failed</p>
                  <p className="text-xs">{submitError}</p>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 pt-2">
              <Button type="submit" disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                Create customer
              </Button>
              <Button type="button" variant="ghost" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── List page ───────────────────────────────────────────────────────────

export function CustomersListPage() {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const listKey = location.pathname + location.search;
  useListStateRestore({ key: listKey });
  const initialQuery = params.get("q") ?? "";
  const initialTier = (params.get("tier") ?? "") as "" | "1" | "2" | "3";
  const initialTab: ArchivedTab = params.get("tab") === "archived" ? "archived" : "active";
  const [queryRaw, setQueryRaw] = useState(initialQuery);
  const [tab, setTab] = useState<ArchivedTab>(initialTab);
  const [modalOpen, setModalOpen] = useState(false);
  const debouncedQuery = useDebouncedValue(queryRaw, DEBOUNCE_MS);
  const tier = initialTier;
  const { isAdmin } = useIsAdmin();

  // Mirror search state into URL (replace) for shareable links.
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (debouncedQuery) next.set("q", debouncedQuery);
    else next.delete("q");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);

  // Mirror archived tab into URL.
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (tab === "archived") next.set("tab", "archived");
    else next.delete("tab");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const args = useMemo(() => ({
    query: debouncedQuery,
    ...(tier ? { tier: Number(tier) as 1 | 2 | 3 } : {}),
    limit: 50,
  }), [debouncedQuery, tier]);

  const { data, error, loading, refresh } = useMcp(
    () => mcp.searchCustomers(args),
    [debouncedQuery, tier, tab],
  );

  useRealtimeRefresh(
    [
      { schema: "crm", table: "customers" },
      { schema: "samples", table: "customer_tags" },
    ],
    refresh,
  );

  // The search verb returns customers regardless of archived_at. Filter on
  // the client until the verb is bumped to accept an `archived` param.
  // (Most archived customers will be a small slice, so client-side is fine.)
  const customers = useMemo(() => {
    const rows = data?.customers ?? [];
    return rows.filter((c) => {
      const archivedAt = (c as Record<string, unknown>).archived_at as string | null | undefined;
      const isArchived = Boolean(archivedAt);
      return tab === "archived" ? isArchived : !isArchived;
    });
  }, [data, tab]);

  const updateTier = (value: "" | "1" | "2" | "3") => {
    const next = new URLSearchParams(params);
    if (value) next.set("tier", value);
    else next.delete("tier");
    setParams(next, { replace: true });
  };

  return (
    <div className="space-y-6">
      <ReadOnlyBanner />
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Customers</h2>
          <p className="text-sm text-muted-foreground">
            Search retail customers by name or slug.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <PermissionGate>
              <Button onClick={() => setModalOpen(true)}>
                <Plus className="h-4 w-4" />
                New customer
              </Button>
            </PermissionGate>
          )}
          {!isAdmin && (
            // Non-admins still see the form route (the page itself blocks
            // submission server-side via the admin-only RPC). Keep the
            // navigation button for consistency with the rest of /forms.
            <PermissionGate>
              <Button asChild variant="outline">
                <Link to="/forms/new-customer">
                  <Plus className="h-4 w-4" />
                  New customer
                </Link>
              </Button>
            </PermissionGate>
          )}
        </div>
      </div>

      {/* Active / Archived tab toggle */}
      <div className="flex items-center gap-1 border-b">
        {(["active", "archived"] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={cn(
              "border-b-2 px-3 py-2 text-sm capitalize transition-colors",
              tab === t
                ? "border-primary font-medium text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
            aria-current={tab === t ? "page" : undefined}
          >
            {t}
          </button>
        ))}
      </div>

      <Card>
        <CardHeader className="flex-col gap-3 space-y-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              value={queryRaw}
              onChange={(e) => setQueryRaw(e.target.value)}
              placeholder="Search by name or slug..."
              className="pl-9"
              aria-label="Search customers"
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">Tier</span>
            {TIER_OPTIONS.map((opt) => (
              <button
                key={opt.value || "all"}
                type="button"
                onClick={() => updateTier(opt.value)}
                title={opt.help}
                className={cn(
                  "rounded-full border px-3 py-1 text-xs transition-colors",
                  tier === opt.value
                    ? "border-primary bg-primary/10 font-medium text-primary"
                    : "border-input bg-background text-muted-foreground hover:bg-muted",
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </CardHeader>

        <CardContent>
          {loading && (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
            </div>
          )}

          {!loading && error && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <div>
                <p className="font-medium">Search failed</p>
                <p className="text-xs">{error.message}</p>
              </div>
            </div>
          )}

          {!loading && !error && customers.length === 0 && (
            <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
              <p>
                {tab === "archived"
                  ? "No archived customers."
                  : "Nothing here yet — try a different search or change the tier filter."}
              </p>
            </div>
          )}

          {!loading && !error && customers.length > 0 && (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {customers.map((c) => (
                <CustomerCard key={c.id} customer={c} listKey={listKey} />
              ))}
            </div>
          )}

          {loading && debouncedQuery && (
            <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Searching
            </div>
          )}
        </CardContent>
      </Card>

      <CardTitle className="sr-only">Customer search results</CardTitle>

      <NewCustomerModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={refresh}
      />
    </div>
  );
}
